import { Navigate, Route, useParams } from 'react-router-dom';
import {
  isReservedSalesPointSlug,
  resolveReservedSalesPointCollisionPath,
} from 'pi-kiosk-shared';
import { CheckoutJourneyRoutes } from '../../features/checkout/CheckoutRoutes.js';
import { ScreenLifecycle } from '../../shared/screen/ScreenLifecycle.js';
import { TenantAuthRoutes } from './auth.js';
import {
  ShopScreen,
  ShopsDirectoryScreen,
  TenantSelectScreen,
} from './lazyRouteComponents.js';
import { TenantShopsDirectoryScreen } from '../../features/shops/ShopsDirectoryScreen.js';
import { withRegisteredScreen } from './registryGuard.js';
import { RecoveryRoutes } from './recovery.js';
import { TenantRetailRoutes, withShopRemount } from './retail.js';
import { TenantCatchAllRedirect, Wave1Suspense } from './routeHelpers.js';

export function TenantPickerRoutes(): JSX.Element {
  return withRegisteredScreen(
    'MS-ENTRY-001',
    <Wave1Suspense>
      <TenantSelectScreen />
    </Wave1Suspense>,
  );
}

/** Platform /shops — MS-ENTRY-003 public shops directory. */
export function ShopsDirectoryRoutes(): JSX.Element {
  return withRegisteredScreen(
    'MS-ENTRY-003',
    <Wave1Suspense>
      <ShopsDirectoryScreen />
    </Wave1Suspense>,
  );
}

/**
 * Tenant-relative routes under `/:tenantCode`.
 * Chrome (`CustomerAppShell`) is owned by `PersistentRetailLayout` in App.tsx —
 * do not wrap another shell here (avoids remount flicker on bottom-nav switches).
 */
export function TenantRouteTree(): JSX.Element {
  return (
    <>
      {/* Bare `/:tenantCode` must not render an empty Outlet (black screen). */}
      <Route index element={<Navigate to="shops" replace />} />
      {/* Tenant SP picker — same omnipresent top bar + bottom nav as other tenant pages. */}
      <Route
        path="shops"
        element={withRegisteredScreen(
          'MS-ENTRY-003',
          <TenantShopsDirectoryScreen />,
        )}
      />
      {/* Auth stays inside the shell so bottom nav remains on sign-in / register. */}
      {TenantAuthRoutes()}
      {TenantRetailRoutes({ includeMore: true })}
      {RecoveryRoutes()}
      {CheckoutJourneyRoutes()}
      {TenantKioskSlugRouteElement()}
      {TenantCatchAllRoute()}
    </>
  );
}

/**
 * G14 — reserved hubs (shops/more/browse/…) must never mount Shop as fake
 * `:kioskSlug` / `:productSlug` (e.g. `/{tenant}/shops/{x}`).
 */
export function ReservedKioskSlugShopGuard(): JSX.Element {
  const { tenantCode, kioskSlug } = useParams<{
    tenantCode?: string;
    kioskSlug?: string;
  }>();
  if (kioskSlug !== undefined && isReservedSalesPointSlug(kioskSlug)) {
    return (
      <Navigate
        to={resolveReservedSalesPointCollisionPath(tenantCode ?? '', kioskSlug)}
        replace
      />
    );
  }
  return <ShopScreen />;
}

export function TenantKioskSlugRouteElement(): JSX.Element {
  const shopScreenElement = (): JSX.Element =>
    withRegisteredScreen(
      'MS-HUB-020',
      withShopRemount(
        <Wave1Suspense>
          <ScreenLifecycle screenId="MS-HUB-020">
            <ReservedKioskSlugShopGuard />
          </ScreenLifecycle>
        </Wave1Suspense>,
      ),
    );

  return (
    <>
      {/* More specific product-detail path first — cold-load opens shared DiscoveryOfferDetailSheet via ProductDetailSheet adapter. */}
      <Route path=":kioskSlug/:productSlug" element={shopScreenElement()} />
      <Route path=":kioskSlug" element={shopScreenElement()} />
    </>
  );
}

export function TenantKioskSlugRoute(): JSX.Element {
  return TenantKioskSlugRouteElement();
}

export function TenantCatchAllRoute(): JSX.Element {
  return <Route path="*" element={<TenantCatchAllRedirect />} />;
}
